import type { TaskStatus } from '@saniplus/contracts';

/**
 * The status vocabulary.
 *
 * `DESIGN.md` fixes five status fields and the Field-and-Word Rule: a status is
 * always a solid field carrying a word, and colour is never the only signal.
 * What it did not fix is *which* tone each situation gets — so page mockups
 * drifted, and « À vérifier » ended up on brand blue on one screen and on amber
 * on another. This module is the single answer.
 *
 * The keys are semantic, never copy: `packages/ui` holds no strings. An app
 * looks a situation up here to get the tone, and looks the same situation up in
 * its own i18n catalogue to get the French word.
 */

export const STATUS_TONES = [
  'success',
  'warning',
  'danger',
  'info',
  'neutral',
  'neutral-strong',
] as const;

export type StatusTone = (typeof STATUS_TONES)[number];

/**
 * The eight worker-facing interruptions, and one rule behind them:
 *
 * - `info` means *the product is working as designed and you need do nothing*.
 * - `warning` means *the product is degraded or you can act, and it is not yet
 *   a problem* — a queued punch is not an error.
 * - `danger` means *this will not resolve itself; it ends in a person*.
 * - `neutral` means *nothing is happening*, which is not a failure either.
 */
export const WORKER_STATE_TONE = {
  /**
   * Queued offline; the punch is recorded locally and will sync.
   *
   * AMBER, not blue. The connectivity band sitting immediately above this one
   * states the same fact in `warning`, and two different colours for one fact,
   * eight pixels apart, is the shell arguing with itself. `VariantA-hors-ligne`
   * draws both amber. It is still not an error — amber is the product working
   * as designed on a plant floor with no signal; red is what ends in a person.
   */
  offline: 'warning',
  /** A previous shift was never closed; it blocks the next punch in. */
  staleOpenPunch: 'danger',
  /** Outside the 60/60 punch window. */
  outsideWindow: 'warning',
  /** Nothing scheduled today. */
  noShift: 'neutral',
  /** This device is not the bound device; a supervisor has to reset it. */
  deviceNotRegistered: 'danger',
  /** Too many failed PIN attempts. */
  accountLocked: 'danger',
  /** Location refused or unavailable — never blocks a punch. */
  gpsDenied: 'info',
  /** The server refused a queued punch on sync; the worker must be told. */
  syncRefused: 'danger',
  /** On the clock. */
  onDuty: 'success',
  /** Off the clock, nothing pending. */
  offDuty: 'neutral',
} as const;

export type WorkerState = keyof typeof WORKER_STATE_TONE;

/**
 * The four task outcomes (Phase 5), read by the worker and by the supervisor
 * looking at the same task — so they are ONE map, not two.
 *
 * The severity rule above decides every value: `pending` is the expected state
 * of a task nobody has reached yet, so it is `info` and not amber (a queue
 * where every fresh row shouts teaches people to stop reading amber);
 * `unable` is the one outcome that ends in a person, so it is `danger`;
 * `cancelled` is terminal like `locked` and `archived`, so it is
 * `neutral-strong` rather than plain neutral, which would read as "nothing
 * happened here yet".
 */
export const TASK_STATUS_TONE: Readonly<Record<TaskStatus, StatusTone>> = {
  pending: 'info',
  done: 'success',
  unable: 'danger',
  cancelled: 'neutral-strong',
};

/**
 * Account states, ordered by the only question that matters at 21:50: **does
 * this stop a punch tonight?** A person can be in several at once; the list is
 * a ranking, and `AccountStateChip` renders the winner plus a "+n" overflow.
 *
 * The word is *bloqué*, never *verrouillé* — "Verrouillée" belongs to a pay
 * period, and a supervisor who reads it on a person goes looking for the wrong
 * unlock.
 */
export const ACCOUNT_STATE_ORDER = [
  'blocked',
  'throttled',
  'temporaryPin',
  'inactive',
  'active',
] as const;

export type AccountState = (typeof ACCOUNT_STATE_ORDER)[number];

export const ACCOUNT_STATE_TONE: Readonly<Record<AccountState, StatusTone>> = {
  blocked: 'danger',
  throttled: 'warning',
  temporaryPin: 'info',
  inactive: 'neutral',
  active: 'success',
};

/** The D-04 settings cascade. */
export type SourceKind = 'site' | 'contract' | 'platform';

/**
 * The one system-wide role a person holds (Phase 4, `users.role`) — the
 * assignable set the design system renders. `team_lead` is reserved in the
 * data model and deliberately absent here, so no screen can offer it.
 */
export type UserRole = 'worker' | 'supervisor' | 'admin' | 'platform_admin';
